import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc, addDoc, collection } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function SpareParts() {
  const { spareParts, loading } = useWebData(); 

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);
  
  // 🔥 Add Part State
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPart, setNewPart] = useState({ partName: "", partNo: "", compatibleModels: "", price: "", officeStock: "" });

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC
  // ==========================================
  useEffect(() => {
    if (!spareParts) return;

    let result = [...spareParts];

    // Search Logic
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(i => {
            const dateStr = i.createdAt ? new Date(i.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";
            
            const row = `${dateStr} ${i.partName} ${i.partNo} ${i.compatibleModels} ${i.price} ${i.officeStock}`.toLowerCase();
            return row.includes(lower);
        });
    }

    // Date Logic
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.createdAt || ""; 
            if (!rawDate) return false;
            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }
            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    result.sort((a, b) => (a.partName || "").localeCompare(b.partName || ""));
    setFiltered(result);
  }, [spareParts, searchTerm, viewMode, selectedDate, selectedYear]);

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Part_Name: i.partName, 
        Part_No: i.partNo, 
        Compatible_Models: i.compatibleModels,
        Price: i.price, 
        Office_Stock: i.officeStock, 
        Employee_Stock: i.myStock || 0,
        Date_Added: formatDate(i.createdAt)
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SpareParts");
    XLSX.writeFile(wb, `Inventory_Report_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  const handleAddPart = async () => {
      if(!newPart.partName || !newPart.price) return alert("Name & Price required!");
      try {
          await addDoc(collection(db, "spare_parts"), {
              ...newPart,
              price: Number(newPart.price),
              officeStock: Number(newPart.officeStock) || 0,
              createdAt: new Date().toISOString()
          });
          alert("Part Added ✅");
          setNewPart({ partName: "", partNo: "", compatibleModels: "", price: "", officeStock: "" });
          setShowAddModal(false);
      } catch (e) { alert("Error adding part"); }
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "spare_parts", editData.id), {
              partName: editData.partName,
              partNo: editData.partNo,
              compatibleModels: editData.compatibleModels,
              price: editData.price,
              officeStock: parseInt(editData.officeStock) || 0
          });
          alert("Part Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const updateOfficeStock = async (id, newStock) => {
      const stock = parseInt(newStock);
      if(!isNaN(stock)) await updateDoc(doc(db, "spare_parts", id), { officeStock: stock });
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this part?")) await deleteDoc(doc(db, "spare_parts", id));
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (iso) => {
      if(!iso) return "-";
      const date = new Date(iso);
      return isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#2c3e50", display:"flex", alignItems:"center"}}>
                🔩 Spare Parts <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <div style={{display:"flex", gap:"10px"}}>
                <button onClick={() => setShowAddModal(true)} style={btnAdd}>➕ Add Part</button>
                <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
            </div>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            <input type="text" placeholder="🔍 Search Part Name, No..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#2c3e50", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Part Name</th>
                    <th style={thStyle}>Part No</th>
                    <th style={thStyle}>Models</th>
                    <th style={thStyle}>Price (₹)</th>
                    <th style={thStyle}>My Stock</th>
                    <th style={thStyle}>Office Stock</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="7" style={{padding:"20px", textAlign:"center", color:"#999"}}>No parts found.</td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={{...tdStyle, fontWeight:"bold", textAlign:"left"}}>{row.partName}</td>
                    <td style={tdStyle}>{row.partNo}</td>
                    <td style={tdStyle}>{row.compatibleModels}</td>
                    <td style={{...tdStyle, color:"green", fontWeight:"bold"}}>{row.price}</td>
                    <td style={{...tdStyle, color:"#8e44ad", fontWeight:"bold"}}>{row.myStock || 0}</td>
                    
                    <td style={{padding:"2px", border:"1px solid #ddd", width:"80px"}}>
                        <input type="number" defaultValue={row.officeStock} onBlur={(e)=>updateOfficeStock(row.id, e.target.value)}
                            style={{width:"60px", textAlign:"center", border:"1px solid #ccc", padding:"4px", borderRadius:"4px"}} />
                    </td>
                    
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px"}}>
                            <button onClick={() => setViewData(row)} style={btnIcon} title="View">👁️</button>
                            <button onClick={() => setEditData(row)} style={btnIconEdit} title="Edit">✏️</button>
                            <button onClick={() => handleDelete(row.id)} style={btnIconDelete} title="Delete">🗑️</button>
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{marginTop:0, color:"#2c3e50"}}>{viewData.partName}</h3>
            <div style={{fontSize:"14px", lineHeight:"1.6"}}>
                <p><strong>🔢 Part No:</strong> {viewData.partNo}</p>
                <p><strong>🛠️ Compatible Models:</strong> {viewData.compatibleModels}</p>
                <p><strong>💰 Price:</strong> ₹{viewData.price}</p>
                <hr style={{border:"0.5px solid #eee"}}/>
                <div style={{display:"flex", gap:"20px"}}>
                    <p style={{color:"#8e44ad"}}><strong>👤 My Stock:</strong> {viewData.myStock || 0}</p>
                    <p style={{color:"#2c3e50"}}><strong>🏢 Office Stock:</strong> {viewData.officeStock || 0}</p>
                </div>
            </div>
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {(editData || showAddModal) && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#f39c12", marginTop:0}}>{editData ? "✏️ Edit Part" : "➕ Add New Part"}</h3>
            
            <label style={labelStyle}>Part Name</label>
            <input type="text" value={editData ? editData.partName : newPart.partName} onChange={(e)=> editData ? setEditData({...editData, partName:e.target.value}) : setNewPart({...newPart, partName:e.target.value})} style={modalInput}/>
            
            <div style={{display:"flex", gap:"5px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Part No</label>
                    <input type="text" value={editData ? editData.partNo : newPart.partNo} onChange={(e)=> editData ? setEditData({...editData, partNo:e.target.value}) : setNewPart({...newPart, partNo:e.target.value})} style={modalInput}/>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Price</label>
                    <input type="number" value={editData ? editData.price : newPart.price} onChange={(e)=> editData ? setEditData({...editData, price:e.target.value}) : setNewPart({...newPart, price:e.target.value})} style={modalInput}/>
                </div>
            </div>

            <label style={labelStyle}>Compatible Models</label>
            <input type="text" value={editData ? editData.compatibleModels : newPart.compatibleModels} onChange={(e)=> editData ? setEditData({...editData, compatibleModels:e.target.value}) : setNewPart({...newPart, compatibleModels:e.target.value})} style={modalInput}/>

            <label style={labelStyle}>Office Stock</label>
            <input type="number" value={editData ? editData.officeStock : newPart.officeStock} onChange={(e)=> editData ? setEditData({...editData, officeStock:e.target.value}) : setNewPart({...newPart, officeStock:e.target.value})} style={modalInput}/>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=> { setEditData(null); setShowAddModal(false); }} style={btnCancel}>Cancel</button>
                <button onClick={editData ? handleSaveEdit : handleAddPart} style={btnSave}>{editData ? "Update" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};
const btnAdd={backgroundColor:"#e67e22", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px", marginRight:"10px"};

const btnIcon={backgroundColor:"#3498db", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#f39c12", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"400px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnSave={backgroundColor:"#2c3e50", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default SpareParts;